/**
 * useInventoryCacheBuster
 *
 * Lyssnar på `cache_signals.product_inventory` via Supabase realtime och
 * kör `invalidateInventoryCaches` så fort backend bumpar signalen efter en
 * lyckad full sync.
 *
 * Fallback: om realtime-eventet inte tas emot (offline tab, dropped socket)
 * kör vi en kort poll var 10:e sek under 90 sek när vi misstänker att en sync
 * just körts (markerat lokalt vid trigger eller via cross-tab BroadcastChannel).
 */
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  invalidateInventoryCaches,
  type CacheSignalMeta,
} from "@/lib/inventoryCacheInvalidation";

const SIGNAL_KEY = "product_inventory";
const FALLBACK_POLL_MS = 10_000;
const FALLBACK_DURATION_MS = 90_000;

export function useInventoryCacheBuster(): void {
  const queryClient = useQueryClient();
  const lastVersionRef = useRef<number | null>(null);
  const fallbackTimerRef = useRef<number | null>(null);
  const fallbackUntilRef = useRef<number>(0);

  useEffect(() => {
    let cancelled = false;

    void supabase
      .from("cache_signals")
      .select("version, metadata")
      .eq("key", SIGNAL_KEY)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        lastVersionRef.current = (data?.version as number | undefined) ?? 0;
      });

    const applySignal = (
      version: number,
      meta: CacheSignalMeta | null,
      source: "realtime" | "fallback_poll",
    ): void => {
      if (lastVersionRef.current !== null && version <= lastVersionRef.current) return;
      lastVersionRef.current = version;
      invalidateInventoryCaches(queryClient, { version, source, meta });
    };

    // --- Realtime ---------------------------------------------------------
    const channel = supabase
      .channel(`cache-signals-${SIGNAL_KEY}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "cache_signals",
          filter: `key=eq.${SIGNAL_KEY}`,
        },
        (payload) => {
          const row = payload.new as
            | { version?: number; metadata?: CacheSignalMeta | null }
            | null;
          if (typeof row?.version !== "number") return;
          applySignal(row.version, row.metadata ?? null, "realtime");
        },
      )
      .subscribe();

    // --- Fallback-poll ----------------------------------------------------
    // Triggas via window-event "inventory-sync-pending" (manuell sync-knapp)
    // eller cross-tab via BroadcastChannel. Pollar var 10:e sek i upp till
    // 90 sek och ber DB om aktuell version — säkerhetsnät om realtime missar.
    const startFallback = (): void => {
      fallbackUntilRef.current = Date.now() + FALLBACK_DURATION_MS;
      if (fallbackTimerRef.current != null) return;
      const tick = async () => {
        if (Date.now() > fallbackUntilRef.current) {
          if (fallbackTimerRef.current != null) {
            window.clearInterval(fallbackTimerRef.current);
            fallbackTimerRef.current = null;
          }
          return;
        }
        const { data } = await supabase
          .from("cache_signals")
          .select("version, metadata")
          .eq("key", SIGNAL_KEY)
          .maybeSingle();
        if (typeof data?.version === "number") {
          applySignal(data.version, (data.metadata ?? null) as CacheSignalMeta | null, "fallback_poll");
        }
      };
      fallbackTimerRef.current = window.setInterval(tick, FALLBACK_POLL_MS);
      void tick();
    };

    const onPending = (): void => startFallback();
    window.addEventListener("inventory-sync-pending", onPending);

    let bc: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== "undefined") {
      bc = new BroadcastChannel("inventory-sync");
      bc.onmessage = (ev) => {
        if (ev.data === "pending") startFallback();
      };
    }

    return () => {
      cancelled = true;
      window.removeEventListener("inventory-sync-pending", onPending);
      if (fallbackTimerRef.current != null) {
        window.clearInterval(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
      bc?.close();
      void supabase.removeChannel(channel);
    };
  }, [queryClient]);
}

/**
 * Anropa när en sync precis triggats — sätter igång fallback-pollen så att
 * vi garanterat invaliderar även om realtime-eventet aldrig kommer fram.
 */
export function notifyInventorySyncPending(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("inventory-sync-pending"));
  if (typeof BroadcastChannel !== "undefined") {
    try {
      const bc = new BroadcastChannel("inventory-sync");
      bc.postMessage("pending");
      bc.close();
    } catch {
      /* noop */
    }
  }
}
